import * as React from 'react';
import { useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Paper, { PaperProps } from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { pxToRem } from 'src/common/utils/fonts';
import { omit } from 'lodash';

const filterArray = (options: Option[]) => {
  return options.reduce((p: Option[], c: Option) => {
    if (!p.some((el: Option) => el.id === c.id)) {
      p.push(c);
    }
    return p;
  }, []);
};

const filterOptions = (options: Option[], inputValue: string) => {
  const itemsFilteredByName = options.filter((o: Option) =>
    o.label.toLowerCase().includes(inputValue.toLowerCase()),
  );
  return filterArray(itemsFilteredByName);
};

export type Option = {
  label: string;
  id: number | string;
};

export type SelectWithSearchProps = {
  value: Option | null;
  options: Option[];
  onChange: (value: Option | null) => void;
  onTextInputChange?: (value: string) => void;
  inputValue?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

const AutocompetePaper = (props: PaperProps) => {
  return (
    <Paper
      {...props}
      sx={{
        borderRadius: pxToRem(1),
        boxShadow: '0px 0px 12px 0px #0000001F',
        '.MuiAutocomplete-listbox': {
          p: 0,
          borderRadius: pxToRem(4),
        },
        overflowX: 'clip',
        '.autocomplete-list-box::-webkit-scrollbar': {
          width: pxToRem(12),
          transition: '0.2s',
        },
        '.autocomplete-list-box::-webkit-scrollbar-thumb': {
          borderRadius: pxToRem(8),
          backgroundColor: '#d9dbdd',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '.autocomplete-list-box::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#B6BABA',
          border: 0,
        },
      }}
    />
  );
};

const SelectOption = ({
  selected,
  label,
  ...props
}: Omit<React.HTMLAttributes<HTMLLIElement>, 'className'> & {
  key: any;
} & { selected: boolean; label: string }) => {
  return (
    <Box
      component="li"
      {...props}
      sx={{
        width: 'inherit',
        pl: 4,
        pr: 4,
        pb: 8,
        pt: 6,
        height: pxToRem(36),
        backgroundColor: ({ palette }) => (selected ? palette.grey[200] : palette.background.paper),
        '.close-icon': { display: 'none', width: pxToRem(24), height: pxToRem(24) },
        '.hint-icon': { display: 'block', width: pxToRem(24), height: pxToRem(24) },
        '&:hover': {
          backgroundColor: ({ palette }) => palette.grey[100],
          '.close-icon': {
            display: 'flex',
            borderRadius: pxToRem(27),
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '.hint-icon': { display: 'none' },
        },
        pointerEvents: 'initial',
      }}
    >
      <Stack direction="row" alignItems="center" alignContent="center" spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: '100%', ml: '4px !important' }}
          data-testid="multiselect-option"
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textWrap: 'nowrap',
              maxWidth: '100%',
            }}
          >
            <Typography variant="text1" color="text.primary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
  options,
  value,
  inputValue,
  onChange,
  onTextInputChange,
  placeholder,
  sx,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Autocomplete
      data-testid={props['data-testid'] ?? 'select-with-search'}
      onChange={(_event, value) => {
        onChange(value);
        onTextInputChange?.('');
      }}
      inputValue={inputValue}
      onInputChange={(_event, value, reason) => {
        if (value.startsWith(' ')) {
          value = value.trimStart();
        }

        if (reason !== 'reset') {
          onTextInputChange?.(value);
        }
      }}
      isOptionEqualToValue={(option, testOption) => {
        return option.id === testOption.id;
      }}
      options={options}
      value={value}
      selectOnFocus
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      open={open}
      getOptionLabel={(option) => option.label}
      filterOptions={(options, params) =>
        filterArray([...filterOptions(options, params.inputValue)])
      }
      renderOption={({ ...props }, option, { selected }) => {
        const propsWitouhClassNames = omit(props, 'className');
        return <SelectOption {...propsWitouhClassNames} selected={selected} label={option.label} />;
      }}
      sx={{
        bgcolor: (theme) => theme.palette.background.paper,
        '.MuiAutocomplete-inputRoot': {
          py: 5,
          pl: 12,
          height: 'auto',
          minHeight: pxToRem(40),
        },
        '.MuiOutlinedInput-notchedOutline': {
          borderRadius: pxToRem(4),
        },
        ...sx,
      }}
      slots={{
        paper: AutocompetePaper,
      }}
      ListboxProps={{
        className: 'autocomplete-list-box',
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder={placeholder}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                sx: { p: '0 !important' },
              },
            }}
          />
        );
      }}
    />
  );
};
